// =========================================================================
// `<PhoneInput />` — country-code dropdown + national-number text input
// that always emits an E.164 string (`+<dial><digits>`).
//
// Why this exists:
//   Every messaging provider in this repo (Sendblue today, Twilio
//   tomorrow) rejects anything that isn't E.164. Free-form phone inputs
//   that say "Phone (E.164)" with a `+15555550100` placeholder are
//   regularly filled out as `7073914000` — no plus, no country code —
//   and the SMS leg of the calendar fanout (or any other notify
//   pipeline) silently fails downstream.
//
//   Fixing this at the input layer is the only fix that scales: a US-
//   preselected dropdown to the left of the digits makes the country
//   code visible AND mandatory, and the component assembles the E.164
//   string itself so callers can't forget the `+`.
//
// Public API:
//
//   <PhoneInput
//     value="+17073914000"          // E.164 in, E.164 out
//     onChange={(e164) => void}     // called with assembled '+<dial><digits>' or ''
//     defaultCountry="US"           // ISO-3166 alpha-2; defaults to 'US'
//     placeholder="(555) 555-0100"  // optional, applied to the digits input
//     id="appt-guest-phone"         // optional, forwarded to the digits input
//     disabled={false}              // optional
//     name="phone"                  // optional, forwarded to the digits input
//     required={false}              // optional
//   />
//
// `value`:
//   - Empty string ('') renders the digit field blank and the dropdown
//     on the `defaultCountry`. `onChange` will not fire until the user
//     types at least one digit.
//   - A full E.164 string (`+<1-3 dial><digits>`) is parsed back into
//     country + national portion by longest-prefix-match against the
//     dial codes in `COUNTRIES`. Unrecognised dial codes fall back to
//     `defaultCountry` and show the digits portion verbatim.
//   - A bare digit string (no `+`) is treated as a national-format
//     entry under `defaultCountry`. We strip the leading `+` and any
//     non-digit characters before storing.
//
// `onChange`:
//   - Fires with the assembled E.164 string on every keystroke and
//     every dropdown change.
//   - Fires with '' when the user empties the digit field (so callers
//     can store NULL instead of `'+1'` in the DB).
//
// Validation:
//   The component does NOT enforce per-country national lengths. E.164
//   permits 4–15 total digits worldwide and per-country lengths vary
//   too much (Germany's NDC alone spans 2-5 digits) for a UI helper.
//   Callers that need a stricter check should use the exported
//   `isLikelyValidE164(value)` helper.
//
// Curated country list:
//   The `<select>` carries ~50 commonly-used countries (sorted by name,
//   with US/CA at the top as the project's most common defaults).
//   Adding entries is a one-line change to the `COUNTRIES` array. A
//   full ISO-3166 list would be ~250 entries; we'd rather keep the
//   dropdown scannable.
// =========================================================================

import { forwardRef, useId, useMemo } from 'react';
import { cx } from './cx';

// Same shell as Input.tsx so the two halves visually fuse.
const inputBase =
  'bg-paper border border-rule-strong rounded-none px-3 py-2 text-sm text-ink ' +
  'placeholder:text-muted-2 outline-none transition-colors ' +
  'focus:border-ink focus:bg-linen disabled:opacity-50';

export interface CountryDef {
  /** ISO-3166 alpha-2 code, e.g. 'US' */
  iso: string;
  /** Display name, e.g. 'United States' */
  name: string;
  /** Dial code WITHOUT the leading '+', e.g. '1', '44', '7', '852'. */
  dial: string;
  /** Emoji flag. Non-critical, included for visual scan. */
  flag: string;
}

// Curated list. Ordered: top picks first (US/CA/MX), then alphabetical.
// Multiple ISO codes can share a dial code (US + CA + several Caribbean
// territories all share +1); each row in this table is a distinct
// dropdown entry. Parsing in `splitE164` picks the FIRST row whose dial
// matches the longest prefix — so e.g. +1 will always parse to the US
// entry below. Callers who care can flip the dropdown manually.
export const COUNTRIES: readonly CountryDef[] = [
  { iso: 'US', name: 'United States', dial: '1', flag: '🇺🇸' },
  { iso: 'CA', name: 'Canada', dial: '1', flag: '🇨🇦' },
  { iso: 'MX', name: 'Mexico', dial: '52', flag: '🇲🇽' },
  // -- alphabetical from here --
  { iso: 'AR', name: 'Argentina', dial: '54', flag: '🇦🇷' },
  { iso: 'AU', name: 'Australia', dial: '61', flag: '🇦🇺' },
  { iso: 'AT', name: 'Austria', dial: '43', flag: '🇦🇹' },
  { iso: 'BE', name: 'Belgium', dial: '32', flag: '🇧🇪' },
  { iso: 'BR', name: 'Brazil', dial: '55', flag: '🇧🇷' },
  { iso: 'CL', name: 'Chile', dial: '56', flag: '🇨🇱' },
  { iso: 'CN', name: 'China', dial: '86', flag: '🇨🇳' },
  { iso: 'CO', name: 'Colombia', dial: '57', flag: '🇨🇴' },
  { iso: 'CZ', name: 'Czechia', dial: '420', flag: '🇨🇿' },
  { iso: 'DK', name: 'Denmark', dial: '45', flag: '🇩🇰' },
  { iso: 'EG', name: 'Egypt', dial: '20', flag: '🇪🇬' },
  { iso: 'FI', name: 'Finland', dial: '358', flag: '🇫🇮' },
  { iso: 'FR', name: 'France', dial: '33', flag: '🇫🇷' },
  { iso: 'DE', name: 'Germany', dial: '49', flag: '🇩🇪' },
  { iso: 'GR', name: 'Greece', dial: '30', flag: '🇬🇷' },
  { iso: 'HK', name: 'Hong Kong', dial: '852', flag: '🇭🇰' },
  { iso: 'HU', name: 'Hungary', dial: '36', flag: '🇭🇺' },
  { iso: 'IN', name: 'India', dial: '91', flag: '🇮🇳' },
  { iso: 'ID', name: 'Indonesia', dial: '62', flag: '🇮🇩' },
  { iso: 'IE', name: 'Ireland', dial: '353', flag: '🇮🇪' },
  { iso: 'IL', name: 'Israel', dial: '972', flag: '🇮🇱' },
  { iso: 'IT', name: 'Italy', dial: '39', flag: '🇮🇹' },
  { iso: 'JP', name: 'Japan', dial: '81', flag: '🇯🇵' },
  { iso: 'KE', name: 'Kenya', dial: '254', flag: '🇰🇪' },
  { iso: 'MY', name: 'Malaysia', dial: '60', flag: '🇲🇾' },
  { iso: 'NL', name: 'Netherlands', dial: '31', flag: '🇳🇱' },
  { iso: 'NZ', name: 'New Zealand', dial: '64', flag: '🇳🇿' },
  { iso: 'NG', name: 'Nigeria', dial: '234', flag: '🇳🇬' },
  { iso: 'NO', name: 'Norway', dial: '47', flag: '🇳🇴' },
  { iso: 'PK', name: 'Pakistan', dial: '92', flag: '🇵🇰' },
  { iso: 'PE', name: 'Peru', dial: '51', flag: '🇵🇪' },
  { iso: 'PH', name: 'Philippines', dial: '63', flag: '🇵🇭' },
  { iso: 'PL', name: 'Poland', dial: '48', flag: '🇵🇱' },
  { iso: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹' },
  { iso: 'RO', name: 'Romania', dial: '40', flag: '🇷🇴' },
  { iso: 'RU', name: 'Russia', dial: '7', flag: '🇷🇺' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '966', flag: '🇸🇦' },
  { iso: 'SG', name: 'Singapore', dial: '65', flag: '🇸🇬' },
  { iso: 'ZA', name: 'South Africa', dial: '27', flag: '🇿🇦' },
  { iso: 'KR', name: 'South Korea', dial: '82', flag: '🇰🇷' },
  { iso: 'ES', name: 'Spain', dial: '34', flag: '🇪🇸' },
  { iso: 'SE', name: 'Sweden', dial: '46', flag: '🇸🇪' },
  { iso: 'CH', name: 'Switzerland', dial: '41', flag: '🇨🇭' },
  { iso: 'TW', name: 'Taiwan', dial: '886', flag: '🇹🇼' },
  { iso: 'TH', name: 'Thailand', dial: '66', flag: '🇹🇭' },
  { iso: 'TR', name: 'Turkey', dial: '90', flag: '🇹🇷' },
  { iso: 'UA', name: 'Ukraine', dial: '380', flag: '🇺🇦' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971', flag: '🇦🇪' },
  { iso: 'GB', name: 'United Kingdom', dial: '44', flag: '🇬🇧' },
  { iso: 'VN', name: 'Vietnam', dial: '84', flag: '🇻🇳' },
];

function findCountryByIso(iso: string | undefined | null): CountryDef {
  if (iso) {
    const hit = COUNTRIES.find((c) => c.iso === iso.toUpperCase());
    if (hit) return hit;
  }
  return COUNTRIES[0]!; // US fallback
}

/**
 * Parse an E.164-ish string into (country, nationalDigits).
 *
 *   '+17073914000'   → { country: US, national: '7073914000' }
 *   '+447123456789'  → { country: GB, national: '7123456789' }
 *   '7073914000'     → { country: defaultCountry, national: '7073914000' }
 *   ''               → { country: defaultCountry, national: '' }
 *
 * Longest-prefix-match — '+1' wins over no prefix, '+44' wins over '+4'.
 */
export function splitE164(
  value: string,
  defaultIso = 'US',
): { country: CountryDef; national: string } {
  const v = (value ?? '').trim();
  if (!v) return { country: findCountryByIso(defaultIso), national: '' };

  if (v.startsWith('+')) {
    const stripped = v.slice(1).replace(/[^\d]/g, '');
    // Try matches by descending dial length (3, 2, 1) so 'GB +44' beats 'US +1'.
    for (const len of [3, 2, 1]) {
      const candidate = stripped.slice(0, len);
      const hit = COUNTRIES.find((c) => c.dial === candidate);
      if (hit) {
        return { country: hit, national: stripped.slice(len) };
      }
    }
    // Unknown dial code; default to fallback country, show all digits.
    return { country: findCountryByIso(defaultIso), national: stripped };
  }

  // Bare digits — assume default country.
  return {
    country: findCountryByIso(defaultIso),
    national: v.replace(/[^\d]/g, ''),
  };
}

/**
 * E.164 strict validity check (4-15 total digits, leading +, all-digit body).
 * Not country-specific. Useful for callers that want to gate "Send SMS"
 * behind a basic shape test.
 */
export function isLikelyValidE164(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^\+[1-9]\d{3,14}$/.test(value.trim());
}

export interface PhoneInputProps {
  value: string;
  onChange: (e164: string) => void;
  defaultCountry?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Width hint for the country `<select>`. Defaults to a width that fits
   * the flag + dial. Override with Tailwind classes if your layout
   * needs something different.
   */
  selectClassName?: string;
  inputClassName?: string;
  ariaLabel?: string;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    {
      value,
      onChange,
      defaultCountry = 'US',
      placeholder,
      id,
      name,
      required,
      disabled,
      className,
      selectClassName,
      inputClassName,
      ariaLabel,
    },
    ref,
  ) {
    const autoId = useId();
    const inputId = id ?? `phone-${autoId}`;
    const selectId = `${inputId}-country`;

    const { country, national } = useMemo(
      () => splitE164(value ?? '', defaultCountry),
      [value, defaultCountry],
    );

    function emit(nextCountry: CountryDef, nextNational: string): void {
      const digits = nextNational.replace(/[^\d]/g, '');
      if (digits.length === 0) {
        onChange('');
        return;
      }
      onChange(`+${nextCountry.dial}${digits}`);
    }

    return (
      <div className={cx('flex items-stretch gap-0', className)}>
        <select
          id={selectId}
          aria-label="Country code"
          value={country.iso}
          disabled={disabled}
          onChange={(e) => {
            const nextIso = e.currentTarget.value;
            const next = findCountryByIso(nextIso);
            emit(next, national);
          }}
          className={cx(
            inputBase,
            // Right-edge fused with the digits input.
            'border-r-0 pr-2 max-w-[8rem] cursor-pointer',
            selectClassName,
          )}
        >
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.iso}>
              {c.flag} {c.iso} +{c.dial}
            </option>
          ))}
        </select>
        <input
          ref={ref}
          id={inputId}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={national}
          required={required}
          disabled={disabled}
          aria-label={ariaLabel ?? 'Phone number'}
          placeholder={placeholder ?? '(555) 555-0100'}
          onChange={(e) => {
            // Allow visible formatting characters in the input; only
            // strip when assembling E.164 in `emit`.
            const raw = e.currentTarget.value;
            // We still want to show what the user typed, including
            // spaces / parens / dashes. So we don't normalise the
            // visible state here; we only normalise on emit.
            emit(country, raw);
          }}
          className={cx(inputBase, 'flex-1', inputClassName)}
        />
      </div>
    );
  },
);
